import { toast } from "react-toastify";
import apiClient from '../api/apiClient.js';

/**
 * Service de notification pour l'application
 * Utilise react-toastify pour afficher des notifications
 * Consomme les APIs backend pour les données de notifications
 */
class NotificationService {
  constructor() {
    this.toastOptions = {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    };

    this.initialized = false;
    this.notificationPermission = false;

    // Vérifier si les notifications sont supportées
    this.notificationsSupported = "Notification" in window;

    // Initialiser le service
    this.initialize();
  }

  /**
   * Initialise le service de notification
   */
  initialize() {
    console.log("Initialisation du service de notification");
    this.initialized = true;

    // Vérifier la permission pour les notifications push
    if (this.notificationsSupported) {
      this.checkNotificationPermission();
    }

    // Exposer le service à la fenêtre pour un accès facile par d'autres modules
    if (typeof window !== "undefined") {
      window.notificationService = this;
    }
  }

  /**
   * Vérifie si l'utilisateur a autorisé les notifications push
   */
  checkNotificationPermission() {
    try {
      if (Notification.permission === "granted") {
        this.notificationPermission = true;
      } else if (Notification.permission !== "denied") {
        Notification.requestPermission().then((permission) => {
          this.notificationPermission = permission === "granted";
        });
      }
    } catch (error) {
      console.error(
        "Erreur lors de la vérification des permissions de notification:",
        error,
      );
      this.notificationPermission = false;
    }
  }

  /**
   * Récupère les notifications de l'utilisateur
   */
  async getNotifications(params = {}) {
    try {
      const response = await apiClient.get('/notifications', { params });
      
      // Normaliser la réponse pour gérer différentes structures
      const data = response.data;
      
      // Si la réponse est enveloppée dans success/data
      if (data && data.success && data.data) {
        return {
          notifications: data.data.notifications || [],
          pagination: data.data.pagination || { current: 1, pages: 1, total: 0 }
        };
      }
      
      // Si la réponse est directe
      if (data && data.notifications) {
        return {
          notifications: data.notifications || [],
          pagination: data.pagination || { current: 1, pages: 1, total: 0 }
        };
      }
      
      // Fallback
      return {
        notifications: [],
        pagination: { current: 1, pages: 1, total: 0 }
      };
    } catch (error) {
      console.error('Erreur lors de la récupération des notifications:', error);
      // Retourner une structure vide plutôt que de throw
      return {
        notifications: [],
        pagination: { current: 1, pages: 1, total: 0 }
      };
    }
  }

  /**
   * Récupère le nombre de notifications non lues
   */
  async getUnreadCount() {
    try {
      const response = await apiClient.get('/notifications/unread-count');
      return response.data.count;
    } catch (error) {
      console.error('Erreur lors de la récupération du nombre de notifications non lues:', error);
      return 0;
    }
  }

  /**
   * Marque une notification comme lue
   */
  async markAsRead(notificationId) {
    try {
      const response = await apiClient.put(`/notifications/${notificationId}/read`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la notification:', error);
      throw error;
    }
  }

  /**
   * Marque toutes les notifications comme lues
   */
  async markAllAsRead() {
    try {
      const response = await apiClient.put('/notifications/mark-all-read');
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour des notifications:', error);
      throw error;
    }
  }

  /**
   * Supprime une notification
   */
  async deleteNotification(notificationId) {
    try {
      const response = await apiClient.delete(`/notifications/${notificationId}`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la suppression de la notification:', error);
      throw error;
    }
  }

  /**
   * Affiche une notification de succès
   * @param {string} message - Le message à afficher
   * @param {object} options - Options supplémentaires pour la notification
   */
  success(message, options = {}) {
    const toastOptions = { ...this.toastOptions, ...options };
    toast.success(message, toastOptions);

    if (this.notificationPermission && options.showPush) {
      this.sendPushNotification("Succès", message, {
        icon: "/favicon.ico",
      });
    }

    return true;
  }

  /**
   * Affiche une notification d'erreur
   * @param {string} message - Le message à afficher
   * @param {object} options - Options supplémentaires pour la notification
   */
  error(message, options = {}) {
    const toastOptions = { ...this.toastOptions, ...options };
    toast.error(message, toastOptions);

    if (this.notificationPermission && options.showPush) {
      this.sendPushNotification("Erreur", message, {
        icon: "/favicon.ico",
      });
    }

    return true;
  }

  /**
   * Affiche une notification d'information
   * @param {string} message - Le message à afficher
   * @param {object} options - Options supplémentaires pour la notification
   */
  info(message, options = {}) {
    const toastOptions = { ...this.toastOptions, ...options };
    toast.info(message, toastOptions);

    if (this.notificationPermission && options.showPush) {
      this.sendPushNotification("Information", message, {
        icon: "/favicon.ico",
      });
    }

    return true;
  }

  /**
   * Affiche une notification d'avertissement
   * @param {string} message - Le message à afficher
   * @param {object} options - Options supplémentaires pour la notification
   */
  warning(message, options = {}) {
    const toastOptions = { ...this.toastOptions, ...options };
    toast.warning(message, toastOptions);

    if (this.notificationPermission && options.showPush) {
      this.sendPushNotification("Avertissement", message, {
        icon: "/favicon.ico",
      });
    }

    return true;
  }

  /**
   * Envoie une notification push via l'API Notification
   * @param {string} title - Le titre de la notification
   * @param {string} body - Le contenu de la notification
   * @param {object} options - Options supplémentaires pour la notification
   */
  sendPushNotification(title, body, options = {}) {
    try {
      if (!this.notificationsSupported || !this.notificationPermission) {
        return false;
      }

      const notification = new Notification(title, {
        body,
        icon: options.icon || "/favicon.ico",
        ...options,
      });

      notification.onclick = () => {
        window.focus();
        notification.close();
        if (options.onClick) {
          options.onClick();
        }
      };

      return true;
    } catch (error) {
      console.error("Erreur lors de l'envoi de la notification push:", error);
      return false;
    }
  }

  /**
   * Demande la permission pour les notifications push
   * @returns {Promise<boolean>} - True si la permission est accordée
   */
  async requestPermission() {
    if (!this.notificationsSupported) {
      return false;
    }

    try {
      const permission = await Notification.requestPermission();
      this.notificationPermission = permission === "granted";
      return this.notificationPermission;
    } catch (error) {
      console.error("Erreur lors de la demande de permission:", error);
      return false;
    }
  }

  /**
   * Traite une notification reçue via WebSocket
   */
  processWebSocketNotification(data) {
    const { type, notification } = data;
    
    if (type === 'new_notification') {
      // Afficher un toast pour la nouvelle notification
      const priority = notification.priority || 'medium';
      let toastType = 'info';
      
      switch (priority) {
        case 'critical':
        case 'high':
          toastType = 'error';
          break;
        case 'medium':
          toastType = 'info';
          break;
        case 'low':
          toastType = 'success';
          break;
      }

      this[toastType](notification.title, {
        autoClose: priority === 'critical' ? false : 5000,
        showPush: notification.metadata?.playSound
      });

      // Écouter le son uniquement si l'utilisateur est actif
      if (this.canPlaySound()) {
        this.playNotificationSound();
      }

      // Déclencher un événement personnalisé pour mettre à jour l'UI
      window.dispatchEvent(new CustomEvent('newNotification', {
        detail: notification
      }));
    }
  }

  /**
   * Vérifie si on peut jouer un son
   */
  canPlaySound() {
    return typeof Audio !== 'undefined';
  }

  /**
   * Joue un son de notification
   */
  playNotificationSound() {
    try {
      const audio = new Audio('/sounds/notification.mp3');
      audio.volume = 0.3;
      audio.play().catch(error => {
        console.log('Impossible de jouer le son de notification:', error);
      });
    } catch (error) {
      console.log('Son de notification non disponible:', error);
    }
  }

  /**
   * Formate les données des notifications pour l'affichage
   * @param {Array} notifications - La liste des notifications à formater
   * @returns {Array} - La liste des notifications formatées
   */
  formatNotificationData(notifications) {
    if (!Array.isArray(notifications)) {
      console.warn("formatNotificationData a reçu une valeur non-tableau:", notifications);
      return [];
    }
    
    return notifications.map((n) => ({
      ...n,
      timeAgo: this.getTimeAgo(n.createdAt),
      icon: this.getNotificationIcon(n.type, n.subType),
      color: this.getNotificationColor(n.priority),
    }));
  }

  /**
   * Calcule le temps écoulé depuis une date
   */
  getTimeAgo(date) {
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    
    if (days > 0) return `${days}j`;
    if (hours > 0) return `${hours}h`;
    if (minutes > 0) return `${minutes}min`;
    return 'À l\'instant';
  }

  /**
   * Récupère l'icône appropriée pour un type de notification
   */
  getNotificationIcon(type, subType) {
    // Retourne le type pour que NotificationItem.jsx puisse gérer l'affichage de l'icône
    const iconMap = {
      CONNECTION: 'lock',
      TRANSACTION: 'money',
      SECURITY: 'shield',
      SYSTEM: 'settings',
      PROMOTION: 'gift',
      NEWS: 'news',
      EVENT: 'event',
      AFFILIATE: 'users',
      WALLET: 'wallet',
      EXCHANGE_RATE: 'chart',
      ALERT: 'alert',
      AIRDROP: 'gift',
      KYC: 'document',
      P2P: 'exchange',
      ADMIN: 'admin'
    };
    
    return iconMap[type] || 'default';
  }

  /**
   * Récupère la couleur appropriée pour une priorité
   */
  getNotificationColor(priority) {
    const colorMap = {
      critical: 'text-red-500',
      high: 'text-orange-500',
      medium: 'text-blue-500',
      low: 'text-green-500'
    };
    
    return colorMap[priority] || 'text-blue-500';
  }
}

export const notificationService = new NotificationService();

// Exposer le service à la fenêtre pour un accès facile par d'autres modules
if (typeof window !== "undefined") {
  window.notificationService = notificationService;
}

export default notificationService;
